import { Component, OnInit, Input } from '@angular/core';
import { PlanningInformationType, PlanningInformationSubtype, PlanningTreatmentComment } from '../../../shared/services/planning.service';
import { Validators, FormGroup, FormBuilder, FormControl, AbstractControl, FormArray } from '@angular/forms';
import { FacilityType, FacilityTypeService } from '../../../shared/services/facility-type.service';
import { FacilityName, FacilityNameService } from '../../../shared/services/facility-name.service';
import { Observable } from 'rxjs';
import { asEnumerable } from 'linq-es2015';
import { ExistingTreatmentData } from '../request-planning-modal.component';

@Component({
  selector: 'app-request-planning-treatment',
  templateUrl: './request-planning-treatment.component.html'
})
export class RequestPlanningTreatmentComponent implements OnInit {
  @Input() existingTreatmentFormData: ExistingTreatmentData;
  @Input() treatmentFormGroup: FormGroup;
  public facilityTypes: FacilityType[] = [];
  public facilityNames: FacilityName[] = [];

  public get flatInstructions() { return this.treatmentFormGroup.get("flatInstructions") as FormGroup };
  public get flatInstructionsSubstrate() { return this.flatInstructions.get("substrate") };
  public get flatInstructionsIrrigation() { return this.flatInstructions.get("irrigation") };
  public get flatInstructionsFertigation() { return this.flatInstructions.get("fertigation") };
  public get flatLocation() { return this.flatInstructions.get("flatLocation") as FormGroup };
  public get flatFacilityType() { return this.flatLocation.get("facilityType") };
  public get flatFacility() { return this.flatLocation.get("facility") };

  public get pottingInstructions() { return this.treatmentFormGroup.get("pottingInstructions") as FormGroup };
  public get pottingInstructionsSubstrate() { return this.pottingInstructions.get("substrate") };
  public get pottingInstructionsIrrigation() { return this.pottingInstructions.get("irrigation") };
  public get pottingInstructionsFertigation() { return this.pottingInstructions.get("fertigation") };
  public get automationPotLocation() { return this.pottingInstructions.get("automationPotLocation") };
  public get automatedFacilityType() { return this.automationPotLocation.get("facilityType") };
  public get automatedFacility() { return this.automationPotLocation.get("facility") };
  public get staticPotLocation() { return this.pottingInstructions.get("staticPotLocation") };
  public get staticFacilityType() { return this.staticPotLocation.get("facilityType") };
  public get staticFacility() { return this.staticPotLocation.get("facility") };

  public get plantHandlingInstructions() { return this.treatmentFormGroup.get("plantHandlingInstructions") as FormGroup };
  public get plantHandlingLighting() { return this.plantHandlingInstructions.get("lighting") };
  public get plantHandlingTemperature() { return this.plantHandlingInstructions.get("temperature") };
  public get plantHandlingHumidity() { return this.plantHandlingInstructions.get("humidity") };

  public get sprayBoothStudyInstructions() { return this.treatmentFormGroup.get("sprayBoothStudyInstructions") as FormGroup };

  constructor(
    private formBuilder: FormBuilder,
    private facilityNameService: FacilityNameService,
    private facilityTypeService: FacilityTypeService ) {}

  ngOnInit() {
    this.buildTreatmentForm();

    Observable.forkJoin(
      this.facilityTypeService.getAll(),
      this.facilityNameService.getAll(),
    ).subscribe(([
      facilityTypes,
      facilityNames,
    ]) => {
      this.facilityTypes = asEnumerable(facilityTypes || [])
                            .Where(ft => ft.isActive)
                            .OrderBy(ft => ft.order)
                            .ToArray();

      this.facilityNames = asEnumerable(facilityNames || [])
                            .Where(fn => fn.isActive)
                            .OrderBy(fn => fn.order)
                            .ThenBy(fn => fn.name)
                            .ToArray();
    })
  }

  public showPlantHandlingInstructions(): boolean {
    return (
      this.plantHandlingLighting !== null ||
      this.plantHandlingTemperature !== null ||
      this.plantHandlingHumidity !== null
    );
  }

  private buildTreatmentForm() {
    this.treatmentFormGroup.addControl('treatmentNumber', new FormControl(this.existingTreatmentFormData.treatmentNumber));
    this.treatmentFormGroup.addControl('flatInstructions', new FormGroup({}));
    this.treatmentFormGroup.addControl('pottingInstructions', new FormGroup({}));
    this.treatmentFormGroup.addControl('plantHandlingInstructions', new FormGroup({}));
    this.treatmentFormGroup.addControl('sprayBoothStudyInstructions', new FormGroup({}));

    if(!this.existingTreatmentFormData.directSeedToPots) {
      this.flatInstructions.addControl('flatLocation', this.formBuilder.group({
        facilityType: this.formBuilder.control(this.existingTreatmentFormData.flatStorageFacilityTypeId, [Validators.required]),
        facility: this.existingTreatmentFormData.flatStorageFacilityTypeId === -1 ?
          this.formBuilder.control(this.existingTreatmentFormData.flatStorageFacilityLocationId) :
          this.formBuilder.control(this.existingTreatmentFormData.flatStorageFacilityLocationId, [Validators.required])
      }));

      this.flatFacilityType.valueChanges.subscribe((value) => {
        if(value === -1) {
          this.flatFacility.clearValidators();
        }
        else {
          this.flatFacility.setValidators(Validators.required);
        }

        this.flatFacility.updateValueAndValidity();
      });
    }

    this.pottingInstructions.addControl('automationPotLocation', this.formBuilder.group({
      facilityType: this.formBuilder.control(this.existingTreatmentFormData.automationPotStorageFacilityTypeId, [Validators.required]),
      facility: this.existingTreatmentFormData.automationPotStorageFacilityTypeId === -1 ?
        this.formBuilder.control(this.existingTreatmentFormData.automationPotStorageLocationId) :
        this.formBuilder.control(this.existingTreatmentFormData.automationPotStorageLocationId, [Validators.required])
    }));

    this.pottingInstructions.addControl('staticPotLocation', this.formBuilder.group({
      facilityType: this.formBuilder.control(this.existingTreatmentFormData.staticPotStorageFacilityTypeId, [Validators.required]),
      facility: this.existingTreatmentFormData.staticPotStorageFacilityTypeId === -1 ?
        this.formBuilder.control(this.existingTreatmentFormData.staticPotStorageLocationId) :
        this.formBuilder.control(this.existingTreatmentFormData.staticPotStorageLocationId, [Validators.required])
    }));

    this.automatedFacilityType.valueChanges.subscribe((value) => {
      if(value === -1) {
        this.automatedFacility.clearValidators();
      }
      else {
        this.automatedFacility.setValidators(Validators.required);
      }

      this.automatedFacility.updateValueAndValidity();
    });

    this.staticFacilityType.valueChanges.subscribe((value) => {
      if(value === -1) {
        this.staticFacility.clearValidators();
      }
      else {
        this.staticFacility.setValidators(Validators.required);
      }

      this.staticFacility.updateValueAndValidity();
    });

    let fiSubstrate = this.getComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Substrate, this.existingTreatmentFormData.comments);
    let fiIrrigation = this.getComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Irrigation, this.existingTreatmentFormData.comments);
    let fiFertigation = this.getComment(PlanningInformationType.FlatInstruction, PlanningInformationSubtype.Fertigation, this.existingTreatmentFormData.comments);
    let piSubstrate = this.getComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Substrate, this.existingTreatmentFormData.comments);
    let piIrrigation = this.getComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Irrigation, this.existingTreatmentFormData.comments);
    let pifertigation = this.getComment(PlanningInformationType.PottingInstruction, PlanningInformationSubtype.Fertigation, this.existingTreatmentFormData.comments);
    let phLighting = this.getComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Lighting, this.existingTreatmentFormData.comments);
    let phTemperature = this.getComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Temperature, this.existingTreatmentFormData.comments);
    let phHumidity = this.getComment(PlanningInformationType.PlantHandlingInstruction, PlanningInformationSubtype.Humidity, this.existingTreatmentFormData.comments);

    if(fiSubstrate) this.flatInstructions.addControl('substrate', fiSubstrate);
    if(fiIrrigation) this.flatInstructions.addControl('irrigation', fiIrrigation);
    if(fiFertigation) this.flatInstructions.addControl('fertigation', fiFertigation);
    if(piSubstrate) this.pottingInstructions.addControl('substrate', piSubstrate);
    if(piIrrigation) this.pottingInstructions.addControl('irrigation', piIrrigation);
    if(pifertigation) this.pottingInstructions.addControl('fertigation', pifertigation);
    if(phLighting) this.plantHandlingInstructions.addControl('lighting', phLighting);
    if(phTemperature) this.plantHandlingInstructions.addControl('temperature', phTemperature);
    if(phHumidity) this.plantHandlingInstructions.addControl('humidity', phHumidity);
  }

  private getComment(type: PlanningInformationType, subtype: PlanningInformationSubtype, comments: PlanningTreatmentComment[]): FormControl {
    if(comments.some(t => t.instructionType === type && t.instructionSubtype === subtype)){
      let comment = comments.find(t => t.instructionType === type && t.instructionSubtype === subtype).comment;
      return this.formBuilder.control(comment, [Validators.required]);
    }
    return null;
  }

  public copyValueFrom(copyArgs: PlanningTreatmentCopyArgs) {
    if(!this.existingTreatmentFormData.directSeedToPots && !copyArgs.directSeedToPots) {
      this.setDynamicFormValue(this.flatInstructionsSubstrate, copyArgs.flatInstructions.substrate);
      this.setDynamicFormValue(this.flatInstructionsFertigation, copyArgs.flatInstructions.fertigation);
      this.setDynamicFormValue(this.flatInstructionsIrrigation, copyArgs.flatInstructions.irrigation);
      this.setDynamicFormValue(this.flatFacilityType, copyArgs.flatInstructions.flatLocation.facilityType);
      this.setDynamicFormValue(this.flatFacility, copyArgs.flatInstructions.flatLocation.facility);
    }

    this.setDynamicFormValue(this.pottingInstructionsSubstrate, copyArgs.pottingInstructions.substrate);
    this.setDynamicFormValue(this.pottingInstructionsFertigation, copyArgs.pottingInstructions.fertigation);
    this.setDynamicFormValue(this.pottingInstructionsIrrigation, copyArgs.pottingInstructions.irrigation);
    this.setDynamicFormValue(this.automatedFacilityType, copyArgs.pottingInstructions.automationPotLocation.facilityType);
    this.setDynamicFormValue(this.automatedFacility, copyArgs.pottingInstructions.automationPotLocation.facility);
    this.setDynamicFormValue(this.staticFacilityType, copyArgs.pottingInstructions.staticPotLocation.facilityType);
    this.setDynamicFormValue(this.staticFacility, copyArgs.pottingInstructions.staticPotLocation.facility);

    this.setDynamicFormValue(this.plantHandlingHumidity, copyArgs.plantHandlingInstructions.humidity);
    this.setDynamicFormValue(this.plantHandlingTemperature, copyArgs.plantHandlingInstructions.temperature);
    this.setDynamicFormValue(this.plantHandlingLighting, copyArgs.plantHandlingInstructions.lighting);
    
    this.treatmentFormGroup.updateValueAndValidity();
  }

  private setDynamicFormValue(formControl: AbstractControl, value: any) {
    if(formControl && value) {
      formControl.setValue(value);
    }
  }

  public copySprayEventsFrom(copyArgs: SprayEventsCopyArgs): void {
    var sprayBoothGroup = this.treatmentFormGroup.get(
      "sprayBoothStudyInstructions"
    );
    if (!sprayBoothGroup) return;

    var sprayEventArrayControl = sprayBoothGroup.get(
      "sprayEventDaysAfterPlantingList"
    ) as FormArray;
    if (!sprayEventArrayControl || !sprayEventArrayControl.controls) return;

    var srcList =
      copyArgs &&
      copyArgs.sprayEventDaysAfterPlanting &&
      Array.isArray(
        copyArgs.sprayEventDaysAfterPlanting.sprayEventDaysAfterPlantingList
      )
        ? copyArgs.sprayEventDaysAfterPlanting.sprayEventDaysAfterPlantingList
        : [];

    var controls = sprayEventArrayControl.controls;
    for (var i = 0; i < controls.length; i++) {
      if (controls[i] && srcList.length > i) {
        controls[i].setValue(srcList[i]);
      }
    }

    this.treatmentFormGroup.updateValueAndValidity();
  }

}

export interface PlanningTreatmentCopyArgs {
  directSeedToPots: boolean;
  flatInstructions: FlatInstructionsCopyArgs;
  pottingInstructions: PottingInstructionsCopyArgs;
  plantHandlingInstructions: PlantHandlingInstructionsCopyArgs;
}

export interface FlatInstructionsCopyArgs {
  substrate?: string;
  irrigation?: string;
  fertigation?: string;
  flatLocation?: LocationCopyArgs;
}

export interface PottingInstructionsCopyArgs {
  substrate?: string;
  irrigation?: string;
  fertigation?: string;
  automationPotLocation: LocationCopyArgs;
  staticPotLocation: LocationCopyArgs;
}

export interface PlantHandlingInstructionsCopyArgs {
  lighting?: string;
  temperature?: string;
  humidity?: string;
}

export interface LocationCopyArgs {
  facilityType: number;
  facility: number;
}

export interface SprayEventsCopyArgs {
  sprayEventDaysAfterPlanting: {
    sprayEventDaysAfterPlantingList: number[];
  };
}


// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/view-request/request-planning-modal/request-planning-treatment/request-planning-treatment.component.ts